import { supabase } from '../lib/supabase';
import { profileService } from './profileService';
import { triggerSavedJobUpdateNotification } from './notificationTriggers';

/**
 * Service for managing saved jobs operations with Supabase
 * Handles CRUD operations for saved jobs, folders, and alerts
 */

/**
 * Save a job to user's saved list
 * @param {string} jobId - ID of the job to save
 * @param {string} folderName - Optional folder to organize the job
 * @param {string} notes - Optional notes about the job
 * @returns {Promise<{success: boolean, data?: object, error?: object}>}
 */
export const saveJob = async (jobId, folderName = null, notes = null) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase?.from('saved_jobs')?.insert({
        user_id: user?.id,
        job_id: jobId,
        folder_name: folderName,
        notes: notes
      })?.select()?.single();

    if (error) {
      // Handle duplicate save gracefully
      if (error?.code === '23505') {
        return {
          success: false,
          error: { message: 'Job already saved', code: 'DUPLICATE' }
        };
      }
      throw error;
    }

    return {
      success: true,
      data: {
        id: data?.id,
        jobId: data?.job_id,
        folderName: data?.folder_name,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      }
    };
  } catch (error) {
    console.error('Error saving job:', error);
    return {
      success: false,
      error: {
        message: error?.message || 'Failed to save job',
        details: error
      }
    };
  }
};

/**
 * Remove a job from saved list
 * @param {string} jobId - ID of the job to unsave
 * @returns {Promise<{success: boolean, error?: object}>}
 */
export const unsaveJob = async (jobId) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { error } = await supabase?.from('saved_jobs')?.delete()?.eq('user_id', user?.id)?.eq('job_id', jobId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error unsaving job:', error);
    return {
      success: false,
      error: {
        message: error?.message || 'Failed to unsave job',
        details: error
      }
    };
  }
};

/**
 * Get all saved jobs for current user
 * @param {string} folderName - Optional filter by folder
 * @returns {Promise<{success: boolean, data?: array, error?: object}>}
 */
export const getSavedJobs = async (folderName = null) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('Not authenticated');
    }

    let query = supabase?.from('saved_jobs')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

    if (folderName) {
      query = query?.eq('folder_name', folderName);
    }

    const { data, error } = await query;

    if (error) throw error;

    return {
      success: true,
      data: data?.map(item => ({
        id: item?.id,
        jobId: item?.job_id,
        folderName: item?.folder_name,
        notes: item?.notes,
        createdAt: item?.created_at,
        updatedAt: item?.updated_at
      }))
    };
  } catch (error) {
    console.error('Error fetching saved jobs:', error);
    return {
      success: false,
      error: {
        message: error?.message || 'Failed to fetch saved jobs',
        details: error
      }
    };
  }
};

/**
 * Check if a job is saved by current user
 * @param {string} jobId - ID of the job to check
 * @returns {Promise<{success: boolean, isSaved?: boolean, error?: object}>}
 */
export const isJobSaved = async (jobId) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      return { success: true, isSaved: false };
    }

    const { data, error } = await supabase?.from('saved_jobs')?.select('id')?.eq('user_id', user?.id)?.eq('job_id', jobId)?.single();

    if (error && error?.code !== 'PGRST116') {
      throw error;
    }

    return {
      success: true,
      isSaved: !!data
    };
  } catch (error) {
    console.error('Error checking if job is saved:', error);
    return {
      success: false,
      error: {
        message: error?.message || 'Failed to check saved status',
        details: error
      }
    };
  }
};

/**
 * Update notes or folder for a saved job
 * @param {string} jobId - ID of the saved job
 * @param {object} updates - Object with folderName and/or notes
 * @returns {Promise<{success: boolean, data?: object, error?: object}>}
 */
export const updateSavedJob = async (jobId, updates) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('Not authenticated');
    }

    const updateData = {};
    if (updates?.folderName !== undefined) updateData.folder_name = updates?.folderName;
    if (updates?.notes !== undefined) updateData.notes = updates?.notes;

    const { data, error } = await supabase?.from('saved_jobs')?.update(updateData)?.eq('user_id', user?.id)?.eq('job_id', jobId)?.select()?.single();

    if (error) throw error;

    return {
      success: true,
      data: {
        id: data?.id,
        jobId: data?.job_id,
        folderName: data?.folder_name,
        notes: data?.notes,
        updatedAt: data?.updated_at
      }
    };
  } catch (error) {
    console.error('Error updating saved job:', error);
    return {
      success: false,
      error: {
        message: error?.message || 'Failed to update saved job',
        details: error
      }
    };
  }
};

/**
 * Create a new folder for organizing saved jobs
 * @param {string} folderName - Name of the folder
 * @param {string} description - Optional description
 * @returns {Promise<{success: boolean, data?: object, error?: object}>}
 */
export const createFolder = async (folderName, description = null) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase?.from('saved_job_folders')?.insert({
        user_id: user?.id,
        folder_name: folderName,
        description: description
      })?.select()?.single();

    if (error) {
      if (error?.code === '23505') {
        return {
          success: false,
          error: { message: 'Folder already exists', code: 'DUPLICATE' }
        };
      }
      throw error;
    }

    return {
      success: true,
      data: {
        id: data?.id,
        folderName: data?.folder_name,
        description: data?.description,
        createdAt: data?.created_at
      }
    };
  } catch (error) {
    console.error('Error creating folder:', error);
    return {
      success: false,
      error: {
        message: error?.message || 'Failed to create folder',
        details: error
      }
    };
  }
};

/**
 * Get all folders for current user
 * @returns {Promise<{success: boolean, data?: array, error?: object}>}
 */
export const getFolders = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase?.from('saved_job_folders')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data: data?.map(folder => ({
        id: folder?.id,
        folderName: folder?.folder_name,
        description: folder?.description,
        createdAt: folder?.created_at
      }))
    };
  } catch (error) {
    console.error('Error fetching folders:', error);
    return {
      success: false,
      error: {
        message: error?.message || 'Failed to fetch folders',
        details: error
      }
    };
  }
};

/**
 * Delete a folder and optionally move jobs to another folder
 * @param {string} folderName - Name of folder to delete
 * @param {string} moveToFolder - Optional folder name to move jobs to
 * @returns {Promise<{success: boolean, error?: object}>}
 */
export const deleteFolder = async (folderName, moveToFolder = null) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('Not authenticated');
    }

    // If moving jobs, update them first
    if (moveToFolder) {
      const { error: updateError } = await supabase?.from('saved_jobs')?.update({ folder_name: moveToFolder })?.eq('user_id', user?.id)?.eq('folder_name', folderName);

      if (updateError) throw updateError;
    } else {
      // Otherwise, set folder_name to null for jobs in this folder
      const { error: updateError } = await supabase?.from('saved_jobs')?.update({ folder_name: null })?.eq('user_id', user?.id)?.eq('folder_name', folderName);

      if (updateError) throw updateError;
    }

    // Delete the folder
    const { error } = await supabase?.from('saved_job_folders')?.delete()?.eq('user_id', user?.id)?.eq('folder_name', folderName);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error deleting folder:', error);
    return {
      success: false,
      error: {
        message: error?.message || 'Failed to delete folder',
        details: error
      }
    };
  }
};

/**
 * Get unread alerts for current user
 * @returns {Promise<{success: boolean, data?: array, error?: object}>}
 */
export const getJobAlerts = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase?.from('job_alerts')?.select('*')?.eq('user_id', user?.id)?.eq('is_read', false)?.order('created_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data: data?.map(alert => ({
        id: alert?.id,
        jobId: alert?.job_id,
        alertType: alert?.alert_type,
        message: alert?.message,
        isRead: alert?.is_read,
        createdAt: alert?.created_at
      }))
    };
  } catch (error) {
    console.error('Error fetching job alerts:', error);
    return {
      success: false,
      error: {
        message: error?.message || 'Failed to fetch alerts',
        details: error
      }
    };
  }
};

/**
 * Mark alert as read
 * @param {string} alertId - ID of the alert
 * @returns {Promise<{success: boolean, error?: object}>}
 */
export const markAlertAsRead = async (alertId) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('Not authenticated');
    }

    const { error } = await supabase?.from('job_alerts')?.update({ is_read: true })?.eq('id', alertId)?.eq('user_id', user?.id);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error marking alert as read:', error);
    return {
      success: false,
      error: {
        message: error?.message || 'Failed to mark alert as read',
        details: error
      }
    };
  }
};

/**
 * Get saved jobs count for current user
 * @returns {Promise<{success: boolean, count?: number, error?: object}>}
 */
export const getSavedJobsCount = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      return { success: true, count: 0 };
    }

    const { data, error } = await supabase?.rpc('get_saved_jobs_count', {
      p_user_id: user?.id
    });

    if (error) throw error;

    return {
      success: true,
      count: data || 0
    };
  } catch (error) {
    console.error('Error getting saved jobs count:', error);
    return {
      success: false,
      error: {
        message: error?.message || 'Failed to get saved jobs count',
        details: error
      }
    };
  }
};

/**
 * Get saved jobs with profile-based compatibility scoring
 */
export const getSavedJobsWithCompatibility = async (userId, filters = {}) => {
  try {
    // Get saved jobs
    const savedJobs = await getSavedJobs(filters?.folderName);

    // Get user profile for compatibility scoring
    const profile = await profileService?.getCompleteProfile(userId);
    
    // Calculate compatibility scores based on profile
    const jobsWithScores = savedJobs?.data?.map(job => {
      let score = calculateCompatibilityScore(job, profile);
      return {
        ...job,
        compatibilityScore: score,
        matchReasons: generateMatchReasons(job, profile, score)
      };
    });

    // Sort by compatibility score
    jobsWithScores?.sort((a, b) => b?.compatibilityScore - a?.compatibilityScore);

    return jobsWithScores;
  } catch (error) {
    console.error('Error getting saved jobs with compatibility:', error);
    throw error;
  }
};

/**
 * Calculate compatibility score between job and profile
 */
export const calculateCompatibilityScore = (job, profile) => {
  let score = 0;
  const weights = {
    skills: 0.3,
    experience: 0.25,
    location: 0.15,
    salary: 0.15,
    workType: 0.15
  };

  // Skills match
  if (profile?.skills && job?.requiredSkills) {
    const userSkills = profile?.skills?.map(s => s?.skillName?.toLowerCase());
    const jobSkills = job?.requiredSkills?.map(s => s?.toLowerCase());
    const matchedSkills = userSkills?.filter(skill => 
      jobSkills?.some(jobSkill => jobSkill?.includes(skill))
    );
    score += (matchedSkills?.length / jobSkills?.length) * weights?.skills * 100;
  }

  // Experience match
  if (profile?.yearsOfExperience && job?.experienceRequired) {
    const expDiff = Math.abs(profile?.yearsOfExperience - job?.experienceRequired);
    score += Math.max(0, (1 - expDiff / 10)) * weights?.experience * 100;
  }

  // Location preference
  if (profile?.jobPreferences?.preferredLocations && job?.location) {
    const locationMatch = profile?.jobPreferences?.preferredLocations?.some(loc =>
      job?.location?.toLowerCase()?.includes(loc?.toLowerCase())
    );
    if (locationMatch) score += weights?.location * 100;
  }

  // Salary match
  if (profile?.expectedSalary && job?.salaryMin && job?.salaryMax) {
    const salaryMatch = profile?.expectedSalary >= job?.salaryMin && 
                       profile?.expectedSalary <= job?.salaryMax;
    if (salaryMatch) score += weights?.salary * 100;
  }

  // Work type preference
  if (profile?.openToRemote && job?.workType === 'remote') {
    score += weights?.workType * 100;
  }

  return Math.round(Math.min(score, 100));
};

/**
 * Generate match reasons based on profile and job
 */
export const generateMatchReasons = (job, profile, score) => {
  const reasons = [];

  if (score >= 80) {
    reasons?.push('Excellent match for your profile');
  }

  // Skills match
  if (profile?.skills && job?.requiredSkills) {
    const userSkills = profile?.skills?.map(s => s?.skillName?.toLowerCase());
    const jobSkills = job?.requiredSkills?.map(s => s?.toLowerCase());
    const matchedSkills = userSkills?.filter(skill => 
      jobSkills?.some(jobSkill => jobSkill?.includes(skill))
    );
    
    if (matchedSkills?.length > 0) {
      reasons?.push(`${matchedSkills?.length} matching skills`);
    }
  }

  // Experience match
  if (profile?.yearsOfExperience && job?.experienceRequired) {
    if (Math.abs(profile?.yearsOfExperience - job?.experienceRequired) <= 2) {
      reasons?.push('Experience level matches');
    }
  }

  // Location preference
  if (profile?.openToRemote && job?.workType === 'remote') {
    reasons?.push('Remote work available');
  }

  // Salary match
  if (profile?.expectedSalary && job?.salaryMin && job?.salaryMax) {
    const salaryMatch = profile?.expectedSalary >= job?.salaryMin && 
                       profile?.expectedSalary <= job?.salaryMax;
    if (salaryMatch) {
      reasons?.push('Salary matches expectations');
    }
  }

  return reasons;
};

/**
 * Notify users when saved jobs are updated
 * This function should be called when job details change
 * @param {string} jobId - The job ID
 * @param {string} updateType - Type of update (status_change, deadline_approaching, salary_update)
 * @param {Object} updateDetails - Additional update information
 */
export async function notifySavedJobUpdate(jobId, updateType, updateDetails = {}) {
  try {
    // Fetch job details
    const { data: job, error: jobError } = await supabase?.from('jobs')?.select(`
        id,
        title,
        companies (
          name
        )
      `)?.eq('id', jobId)?.single();

    if (jobError) throw jobError;

    // Trigger notification for all users who saved this job
    await triggerSavedJobUpdateNotification(jobId, updateType, {
      jobTitle: job?.title,
      companyName: job?.companies?.name,
      updateDetails
    });

    return { success: true };
  } catch (error) {
    console.error('Error notifying saved job update:', error);
    return { success: false, error };
  }
}

/**
 * Monitor job changes and trigger notifications
 * This should be set up as a database trigger or scheduled job
 */
export async function setupSavedJobsMonitoring() {
  try {
    // Subscribe to job updates
    const channel = supabase?.channel('job-updates')?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'jobs'
        },
        async (payload) => {
          const oldJob = payload?.old;
          const newJob = payload?.new;

          // Check for status changes
          if (oldJob?.status !== newJob?.status) {
            await notifySavedJobUpdate(newJob?.id, 'status_change', {
              oldStatus: oldJob?.status,
              newStatus: newJob?.status
            });
          }

          // Check for salary updates
          if (oldJob?.salary_min !== newJob?.salary_min || oldJob?.salary_max !== newJob?.salary_max) {
            await notifySavedJobUpdate(newJob?.id, 'salary_update', {
              oldRange: `${oldJob?.salary_min}-${oldJob?.salary_max}`,
              newRange: `${newJob?.salary_min}-${newJob?.salary_max}`
            });
          }

          // Check for application deadline
          if (newJob?.application_deadline) {
            const daysUntilDeadline = Math.ceil(
              (new Date(newJob.application_deadline) - new Date()) / (1000 * 60 * 60 * 24)
            );

            if (daysUntilDeadline <= 7 && daysUntilDeadline > 0) {
              await notifySavedJobUpdate(newJob?.id, 'deadline_approaching', {
                daysLeft: daysUntilDeadline,
                deadline: newJob?.application_deadline
              });
            }
          }
        }
      )?.subscribe();

    return channel;
  } catch (error) {
    console.error('Error setting up saved jobs monitoring:', error);
    return null;
  }
}

/**
 * Subscribe to real-time saved jobs updates
 * @param {string} userId - User ID to subscribe to
 * @param {function} callback - Callback function to handle updates
 * @returns {function} Unsubscribe function
 */
export const subscribeToSavedJobs = (userId, callback) => {
  const channel = supabase
    ?.channel('saved-jobs-changes')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'saved_jobs',
        filter: `user_id=eq.${userId}`
      },
      async (payload) => {
        const eventType = payload?.eventType;
        const savedJob = payload?.new || payload?.old;

        if (savedJob) {
          // Fetch job details for saved job
          let jobDetails = null;
          if (eventType === 'INSERT' || eventType === 'UPDATE') {
            const { data: job } = await supabase
              ?.from('jobs')
              ?.select(`
                id,
                title,
                description,
                employment_type,
                salary_min,
                salary_max,
                companies (
                  name,
                  logo_url
                ),
                company_locations (
                  city,
                  state,
                  country
                )
              `)
              ?.eq('id', savedJob?.job_id)
              ?.single();

            if (job) {
              jobDetails = {
                id: job?.id,
                title: job?.title,
                description: job?.description,
                employmentType: job?.employment_type,
                salaryMin: job?.salary_min,
                salaryMax: job?.salary_max,
                company: {
                  name: job?.companies?.name,
                  logoUrl: job?.companies?.logo_url
                },
                location: job?.company_locations ? {
                  city: job?.company_locations?.city,
                  state: job?.company_locations?.state,
                  country: job?.company_locations?.country
                } : null
              };
            }
          }

          callback({
            eventType,
            savedJob: {
              id: savedJob?.id,
              userId: savedJob?.user_id,
              jobId: savedJob?.job_id,
              folderName: savedJob?.folder_name,
              notes: savedJob?.notes,
              createdAt: savedJob?.created_at,
              updatedAt: savedJob?.updated_at,
              job: jobDetails
            }
          });
        }
      }
    )
    ?.subscribe();

  // Return unsubscribe function
  return () => {
    supabase?.removeChannel(channel);
  };
};

/**
 * Subscribe to job alerts for saved jobs
 * @param {string} userId - User ID to subscribe to
 * @param {function} callback - Callback function to handle alerts
 * @returns {function} Unsubscribe function
 */
export const subscribeToJobAlerts = (userId, callback) => {
  const channel = supabase
    ?.channel('job-alerts-changes')
    ?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'job_alerts',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        const alert = payload?.new;

        if (alert) {
          callback({
            id: alert?.id,
            userId: alert?.user_id,
            jobId: alert?.job_id,
            alertType: alert?.alert_type,
            message: alert?.message,
            isRead: alert?.is_read,
            createdAt: alert?.created_at
          });
        }
      }
    )
    ?.subscribe();

  // Return unsubscribe function
  return () => {
    supabase?.removeChannel(channel);
  };
};

export default {
  saveJob,
  unsaveJob,
  getSavedJobs,
  isJobSaved,
  updateSavedJob,
  createFolder,
  getFolders,
  deleteFolder,
  getJobAlerts,
  markAlertAsRead,
  getSavedJobsCount,
  getSavedJobsWithCompatibility,
  calculateCompatibilityScore,
  generateMatchReasons,
  notifySavedJobUpdate,
  setupSavedJobsMonitoring,
  subscribeToSavedJobs,
  subscribeToJobAlerts
};
function toggleSavedJob(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: toggleSavedJob is not implemented yet.', args);
  return null;
}

export { toggleSavedJob };